import Dexie, { Table } from 'dexie';

/**
 * Audios de notas de voz grabadas sin conexión (KAN-225).
 *
 * Base IndexedDB propia, fuera de CattleManagementDB y de la sync con
 * Supabase: el audio vive solo en el dispositivo hasta que se transcribe al
 * reconectar (después se borra). Se guarda ArrayBuffer y no Blob porque Safari
 * viejo y fake-indexeddb no clonan Blob; `get` reconstruye el Blob.
 * Spec: docs/superpowers/specs/2026-09-15-notas-de-voz-offline-chat-design.md
 */

export interface StoredVoiceNote {
  /** = ChatMessage.id */
  id: string;
  conversationId: string;
  blob: Blob;
  mimeType: string;
  durationMs: number;
  createdAt: number;
}

interface VoiceNoteRow {
  id: string;
  conversationId: string;
  data: ArrayBuffer;
  mimeType: string;
  durationMs: number;
  createdAt: number;
}

class ChatAudioDatabase extends Dexie {
  voice_notes!: Table<VoiceNoteRow, string>;

  constructor() {
    super('ChatAudioDB');
    this.version(1).stores({ voice_notes: 'id, conversationId, createdAt' });
  }
}

let db: ChatAudioDatabase | null = null;
const getDb = (): ChatAudioDatabase => {
  if (!db) db = new ChatAudioDatabase();
  return db;
};

function blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

export const voiceNoteStore = {
  async save(note: StoredVoiceNote): Promise<void> {
    const data = await blobToArrayBuffer(note.blob);
    await getDb().voice_notes.put({
      id: note.id,
      conversationId: note.conversationId,
      data,
      mimeType: note.mimeType,
      durationMs: note.durationMs,
      createdAt: note.createdAt,
    });
  },

  async get(id: string): Promise<StoredVoiceNote | null> {
    const row = await getDb().voice_notes.get(id);
    if (!row) return null;
    return {
      id: row.id,
      conversationId: row.conversationId,
      mimeType: row.mimeType,
      durationMs: row.durationMs,
      createdAt: row.createdAt,
      blob: new Blob([row.data], { type: row.mimeType }),
    };
  },

  async remove(id: string): Promise<void> {
    await getDb().voice_notes.delete(id);
  },

  async removeMany(ids: string[]): Promise<void> {
    if (ids.length > 0) await getDb().voice_notes.bulkDelete(ids);
  },

  async removeByConversation(conversationId: string): Promise<void> {
    await getDb().voice_notes.where('conversationId').equals(conversationId).delete();
  },

  async listIds(): Promise<string[]> {
    return (await getDb().voice_notes.toCollection().primaryKeys()) as string[];
  },

  /** Pide almacenamiento persistente (best-effort) para que el navegador no borre audios pendientes. */
  async requestPersistence(): Promise<void> {
    try {
      if (typeof navigator !== 'undefined' && navigator.storage?.persist) {
        const already = navigator.storage.persisted ? await navigator.storage.persisted() : false;
        if (!already) await navigator.storage.persist();
      }
    } catch {
      /* best-effort */
    }
  },
};
